// src/views/adminView/contractsView/contractRenewal.js
// ─────────────────────────────────────────────────────────────────────────────
// Lógica PURA de la renovación del contrato y del historial de contratos firmados (sin
// React ni i18n: los textos se devuelven como claves i18n, el componente los pasa por t()).
//
// Fechas: el contrato guarda días calendario anclados a UTC. Las legacy son la medianoche
// UTC de `new Date('YYYY-MM-DD')`; las de la renovación, las 12:00 UTC (firma e inicio) o
// las 23:59:59.999 UTC (término). El día de cualquiera sale de `toISOString().slice(0, 10)`
// y toda la aritmética se hace en UTC: con la hora local, en Chile un término
// '2026-12-31T00:00Z' se lee 30-12 y el "día siguiente" caería encima de la vigencia
// anterior.
// ─────────────────────────────────────────────────────────────────────────────

/** Tamaño máximo del PDF firmado (10 MiB): el mismo tope que valida contracts. */
export const MAX_CONTRACT_PDF_BYTES = 10 * 1024 * 1024;

/** Largo máximo del nombre del archivo que acepta contracts. */
const MAX_FILE_NAME_LENGTH = 150;

const DAY_MS = 24 * 60 * 60 * 1000;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Día calendario 'YYYY-MM-DD' (UTC) de una fecha del contrato, o null. */
export function ymdOf(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

/** Date UTC de un 'YYYY-MM-DD' que existe como día (rechaza 2026-02-31), o null. */
function parseYmd(ymd) {
  const match = YMD_RE.exec(typeof ymd === 'string' ? ymd : '');
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
    ? date
    : null;
}

export const isValidYmd = (ymd) => parseYmd(ymd) !== null;

/** Suma días a un 'YYYY-MM-DD' en aritmética UTC. */
export function addDaysYmd(ymd, days) {
  const date = parseYmd(ymd);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Suma meses con el desborde nativo de Date.UTC (29-feb + 12 meses = 1-mar): así "inicio
 * + 12 meses − 1 día" cierra el 28-feb, que es como se cuenta un año de contrato.
 */
export function addMonthsYmd(ymd, months) {
  const date = parseYmd(ymd);
  if (!date) return null;
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, date.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
}

/** Hoy como día calendario LOCAL: la fecha que el operador tiene delante al firmar. */
export function localTodayYmd(now = new Date()) {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** 'YYYY-MM-DD' → 'dd-mm-yyyy' (el formato de formatDate en es-CL). */
export function formatYmd(ymd) {
  if (!isValidYmd(ymd)) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}-${m}-${y}`;
}

/** Día calendario de una fecha del contrato, formateado; '' si no hay fecha. */
export const formatContractDay = (value) => formatYmd(ymdOf(value));

/**
 * Vigencia del contrato por su término: terminada | por_vencer (≤30 días) | vigente |
 * indefinida (sin término). Mismo criterio que la vigencia del Directorio y DetalleKpis.
 */
export function vigenciaDe(contract, now = Date.now()) {
  const end = contract?.validityPeriod?.endDate;
  if (!end) return 'indefinida';
  const endMs = new Date(end).getTime();
  if (Number.isNaN(endMs)) return 'indefinida';
  if (endMs < now) return 'terminada';
  if (endMs - now <= 30 * DAY_MS) return 'por_vencer';
  return 'vigente';
}

/** La renovación es la acción que corresponde: el contrato terminó o está por terminar. */
export const renovacionUrgente = (contract, now = Date.now()) =>
  ['terminada', 'por_vencer'].includes(vigenciaDe(contract, now));

/**
 * Valores iniciales del formulario de renovación:
 *  · firma = hoy;
 *  · desde = el día siguiente al término actual si el contrato sigue vigente; si no (o si
 *    no tiene término), hoy;
 *  · hasta = desde + 12 meses − 1 día;
 *  · reactivar marcado si el contrato no está activo.
 */
export function renewalDefaults(contract, now = new Date()) {
  const today = localTodayYmd(now);
  const endYmd = ymdOf(contract?.validityPeriod?.endDate);
  const startDate = endYmd && endYmd >= today ? addDaysYmd(endYmd, 1) : today;
  return {
    dateSigned: today,
    startDate,
    endDate: addDaysYmd(addMonthsYmd(startDate, 12), -1),
    noEnd: false,
    reactivate: contract?.status !== 'active',
  };
}

/**
 * Errores del formulario (claves i18n por campo). `today` = localTodayYmd().
 * @returns {{ dateSigned?: string, startDate?: string, endDate?: string }}
 */
export function validateRenewalForm({ dateSigned, startDate, endDate, noEnd }, today) {
  const errors = {};
  if (!isValidYmd(dateSigned)) errors.dateSigned = 'Indica la fecha de firma.';
  else if (dateSigned > today) errors.dateSigned = 'La fecha de firma no puede ser posterior a hoy.';
  if (!isValidYmd(startDate)) errors.startDate = 'Indica desde cuándo rige el contrato.';
  if (!noEnd) {
    if (!isValidYmd(endDate)) {
      errors.endDate = 'Indica hasta cuándo rige o marca «Sin fecha de término».';
    } else if (isValidYmd(startDate) && endDate < startDate) {
      errors.endDate = 'La fecha de término no puede ser anterior al inicio.';
    }
  }
  return errors;
}

/**
 * Problema del archivo elegido por tipo o tamaño (clave i18n), o null. La firma `%PDF-` se
 * revisa aparte porque es asíncrona (contractDocumentFile.hasPdfSignature).
 */
export function pdfFileProblem(file) {
  if (!file) return 'Adjunta el contrato firmado en PDF.';
  const isPdf =
    file.type === 'application/pdf' || (!file.type && /\.pdf$/i.test(file.name || ''));
  if (!isPdf) return 'El archivo debe ser un PDF';
  if (file.size > MAX_CONTRACT_PDF_BYTES) return 'El PDF supera el tamaño máximo (10 MB)';
  if (!file.size) return 'El archivo no es un PDF válido.';
  return null;
}

// Caracteres que los sistemas de archivos no aceptan en un nombre.
const UNSAFE_NAME_CHARS = /[<>:"|?*]/g;

/**
 * Nombre del archivo para el historial y la descarga: sin ruta, separadores ni
 * caracteres de control, terminado en `.pdf` y de hasta 150 caracteres.
 */
export function safePdfFileName(name, fallback = 'contrato-firmado.pdf') {
  const printable = Array.from(String(name ?? ''))
    .filter((ch) => {
      const code = ch.codePointAt(0);
      return code >= 0x20 && code !== 0x7f;
    })
    .join('');
  // Solo el último tramo de una ruta ("C:\fakepath\x.pdf", "a/b/x.pdf").
  const base = printable.split(/[\\/]/).pop();
  const cleaned = base.replace(UNSAFE_NAME_CHARS, '-').replace(/\s+/g, ' ').trim();
  const stem = cleaned.replace(/\.pdf$/i, '').trim();
  if (!stem) return fallback;
  return `${stem.slice(0, MAX_FILE_NAME_LENGTH - '.pdf'.length)}.pdf`;
}

/** Tamaño legible ("850 KB", "1,2 MB"). */
export function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  const fmt = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 1 });
  if (n < 1024 * 1024) return `${fmt.format(Math.round(n / 1024))} KB`;
  return `${fmt.format(n / (1024 * 1024))} MB`;
}

/** Id del operador del contrato (`logisticsCompanyId` llega poblado o como id). */
export function operatorIdOf(contract) {
  const lc = contract?.logisticsCompanyId;
  const id = lc && typeof lc === 'object' ? lc._id : lc;
  return id ? String(id) : null;
}

/** Historial de contratos firmados, del más reciente al más antiguo. */
export function sortSignedDocuments(docs) {
  if (!Array.isArray(docs)) return [];
  return docs
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => doc && doc._id)
    .sort((a, b) => {
      const ta = new Date(a.doc.uploadedAt).getTime() || 0;
      const tb = new Date(b.doc.uploadedAt).getTime() || 0;
      return tb - ta || b.index - a.index;
    })
    .map(({ doc }) => doc);
}

/**
 * Margen al comparar el `uploadedAt` de una entrada (lo pone contracts con su reloj) con la
 * hora del navegador al empezar el envío: absorbe la diferencia entre los dos relojes.
 */
export const RECONCILE_CLOCK_MARGIN_MS = 120 * 1000;

/**
 * Espera antes de la segunda relectura del contrato tras una respuesta perdida: el proxy pudo
 * cortar mientras contracts todavía escribía.
 */
export const RECONCILE_RETRY_MS = 4000;

/**
 * ¿La renovación enviada quedó guardada? Concilia una renovación cuya respuesta no llegó: si el
 * backend la guardó, el reintento no debe repetirla. Solo cuenta la entrada MÁS RECIENTE del
 * historial, la última del arreglo (contracts solo agrega con $push y deduplica contra esa
 * misma), con el mismo archivo, las mismas fechas (el mismo PDF pudo usarse antes con otra
 * vigencia) y subida después de empezar el envío, menos el margen de reloj. Una entrada
 * anterior igual no prueba nada: pudo cargarse antes y quedar reemplazada por otra.
 * @param {object} contract  contrato releído (GET contracts/:id)
 * @param {{ sha256: string, dateSigned: string, startDate: string, endDate: (string|null),
 *           submittedAt: number }} expected  `submittedAt` = Date.now() al empezar el envío
 */
export function hasMatchingSignedDocument(
  contract,
  { sha256, dateSigned, startDate, endDate, submittedAt },
) {
  const docs = Array.isArray(contract?.signedDocuments) ? contract.signedDocuments : [];
  const latest = docs[docs.length - 1];
  if (!latest) return false;
  // Sin un uploadedAt válido (o sin submittedAt) la comparación da false: no se da por hecha.
  const uploadedAt = new Date(latest.uploadedAt).getTime();
  return (
    latest.sha256 === sha256 &&
    ymdOf(latest.dateSigned) === dateSigned &&
    ymdOf(latest.validityPeriod?.startDate) === startDate &&
    ymdOf(latest.validityPeriod?.endDate) === (endDate ?? null) &&
    uploadedAt >= submittedAt - RECONCILE_CLOCK_MARGIN_MS
  );
}

// Errores de la renovación por `code` del backend (claves i18n). Nunca se muestra el texto
// crudo del backend: puede traer detalles internos y no está traducido.
const RENEW_ERROR_BY_CODE = {
  contract_not_found: 'No se encontró el contrato: puede que lo hayan eliminado.',
  contract_changed: 'El contrato cambió mientras renovabas; revisa y vuelve a intentar',
  invalid_dates: 'Las fechas no son válidas: revisa la fecha de firma y la vigencia.',
  invalid_document: 'El documento subido no es válido. Vuelve a adjuntar el PDF.',
  document_too_large: 'El PDF supera el tamaño máximo (10 MB)',
  not_pdf: 'El archivo no es un PDF válido.',
  document_unreachable: 'No se pudo verificar el PDF subido. Vuelve a intentarlo en unos minutos.',
};

const codeOf = (error) =>
  error && typeof error === 'object' && typeof error.code === 'string' ? error.code : null;

/**
 * Respuesta que pudo haberse guardado igual: no llegó (red, timeout) o la cortó el proxy
 * (502/503/504 sin un `code` del handler). Antes de ofrecer reintentar se relee el contrato
 * (y otra vez a los RECONCILE_RETRY_MS si la primera relectura no la encuentra).
 */
export function isAmbiguousRenewFailure({ error, status }) {
  if (status === null || status === undefined) return true;
  return [502, 503, 504].includes(status) && !RENEW_ERROR_BY_CODE[codeOf(error)];
}

/** Texto (clave i18n) de un error definitivo de la renovación. */
export function renewErrorKey({ error, status }) {
  const byCode = RENEW_ERROR_BY_CODE[codeOf(error)];
  if (byCode) return byCode;
  if (status === 403) return 'No tienes permiso para renovar contratos';
  if (status === 409) return RENEW_ERROR_BY_CODE.contract_changed;
  if (status === 400) return 'Revisa los datos de la renovación y vuelve a intentarlo.';
  return 'No se pudo renovar el contrato. Vuelve a intentarlo.';
}
